import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAnalytics } from 'use-analytics';
import { ErrorCodes, type Error } from '../../utils/errorMapper';
import { QueryKey } from '@/Config/api/queryKey';
import { getPendingVerificationEmail } from '@/Domain/utils/pendingVerificationEmail';

type EmailVerificationDto = {
  user_email: string;
};

// Mirrors the backend resend lock (RESEND_MAIL_COOLDOWN_SECONDS).
const RESEND_COOLDOWN_SECONDS = 5 * 60;

export const useVerificationEmail = () => {
  const { track } = useAnalytics();
  const userEmail = getPendingVerificationEmail();

  const [showResendMessage, setShowResendMessage] = useState(false);
  const [apiError, setApiError] = useState<ErrorCodes | undefined>();
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [cooldownSecondsRemaining, setCooldownSecondsRemaining] = useState(0);

  useEffect(() => {
    if (cooldownEndsAt === null) {
      setCooldownSecondsRemaining(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
      setCooldownSecondsRemaining(remaining);
      if (remaining === 0) setCooldownEndsAt(null);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [cooldownEndsAt]);

  const startCooldown = (seconds: number = RESEND_COOLDOWN_SECONDS) =>
    setCooldownEndsAt(Date.now() + seconds * 1000);

  const mutation = useMutation<unknown, Error<EmailVerificationDto>, EmailVerificationDto>({
    mutationKey: [QueryKey.accounts.sendVerification],
    onSuccess: () => {
      track('email_reverification_success', {
        event_category: 'User verification',
        event_label: 'email_verification',
      });
      setShowResendMessage(true);
      setApiError(undefined);
      startCooldown();
    },
    onError: (error) => {
      track('email_reverification_error', {
        event_category: 'User verification',
        event_label: 'email_verification',
      });

      if (error.response) {
        let errorMessage;
        let retryAfterSeconds;
        setShowResendMessage(false);

        Object.entries(error.response?.data?.errors).forEach(([key, value]) => {
          if (key === 'root') {
            value.forEach((errorDetail) => {
              errorMessage = errorDetail.code;
              retryAfterSeconds = errorDetail.options?.retry_after_seconds;
            });
          }
        });

        // The resend lock is a wait, not an error: count down what the server says is left of it.
        if (errorMessage === ErrorCodes.TOO_MANY_REQUESTS) {
          setApiError(undefined);
          startCooldown(
            typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0
              ? Math.min(retryAfterSeconds, RESEND_COOLDOWN_SECONDS)
              : RESEND_COOLDOWN_SECONDS,
          );
        } else {
          setApiError(errorMessage);
        }
      }
    },
  });

  const handleResendClick = (): void => {
    if (userEmail !== null && cooldownSecondsRemaining === 0) {
      track('email_reverification_send', {
        event_category: 'User verification',
        event_label: 'email_verification',
      });

      mutation.mutate({ user_email: userEmail });
    }
  };

  return {
    userEmail,
    apiError,
    handleResendClick,
    showResendMessage,
    isPending: mutation.isPending,
    cooldownSecondsRemaining,
  };
};
